import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toEmbedUrl } from "@/lib/video";
import { toast } from "sonner";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import {
  ArrowLeft, ArrowRight, CheckCircle2, Circle, FileText, Loader2, Video, ChevronDown, Award, Radio, Headphones,
  ExternalLink, CalendarClock, ClipboardCheck, Heart, Clock, Send, ThumbsUp, ThumbsDown, PictureInPicture2,
  RectangleHorizontal, Maximize, Minimize, MoreVertical, Search, Link2, Type, Code2, X, PlaySquare, Lock, Timer, Clock3, Captions,
} from "lucide-react";
import { VideoStage, playerSrc } from "@/components/classroom/VideoStage";
import { LessonTabs } from "@/components/classroom/LessonTabs";
import { HelpVideo } from "@/components/HelpVideo";
import { PresencialInfo, isPresencial } from "@/components/Presencial";

const TYPE_ICON = { video: PlaySquare, file: FileText, embed: Code2, text: Type, audio: Headphones, live: Radio };
const lessonType = (l) => l.content_type || (l.video_url ? "video" : "file");
const readPref = (k, d) => { try { const v = localStorage.getItem(k); return v === null ? d : JSON.parse(v); } catch (e) { return d; } };
const savePref = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch (e) {} };
const CC_LANGS = [["pt", "Português"], ["es", "Español"], ["en", "English"], ["fr", "Français"], ["it", "Italiano"], ["de", "Deutsch"]];
const COUNTRY_NAME = (c) => { try { return new Intl.DisplayNames(["pt-BR"], { type: "region" }).of(c); } catch (e) { return c; } };
const fmtMin = (s) => { s = Math.max(0, Math.round(s || 0)); const m = Math.floor(s / 60); return m ? `${m} min${s % 60 ? ` ${s % 60} s` : ""}` : `${s} s`; };
// regras da aula valem só para o aluno (o servidor manda "required_seconds" apenas para alunos)
const isStudentLesson = (l, preview) => !preview && l && "required_seconds" in l;

function Ring({ pct }) {
  const r = 26, c = 2 * Math.PI * r;
  return (
    <div className="relative h-16 w-16 shrink-0">
      <svg viewBox="0 0 64 64" className="h-16 w-16 -rotate-90">
        <circle cx="32" cy="32" r={r} fill="none" strokeWidth="6" className="stroke-slate-200" />
        <circle cx="32" cy="32" r={r} fill="none" strokeWidth="6" stroke="#1E9E2A" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c * (1 - pct / 100)} />
      </svg>
      <span className="absolute inset-0 grid place-items-center text-sm font-bold text-slate-800">{pct}%</span>
    </div>
  );
}

export default function Classroom({ preview = false, backTo = "" }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const backPath = backTo || (preview ? `/admin/cursos/${id}#aulas` : "/app");
  const [course, setCourse] = useState(null);
  const [completed, setCompleted] = useState([]);
  const [active, setActive] = useState(null); // { moduleId, lesson }
  const [openModules, setOpenModules] = useState({});
  const [examSt, setExamSt] = useState(null);
  const [social, setSocial] = useState(null);
  const [marks, setMarks] = useState({ favorite: [], watch_later: [] });
  const [watermark, setWatermark] = useState(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("todas");
  const [autoAdvance, setAutoAdvance] = useState(() => readPref("iep-autoplay", true));
  const [rate, setRate] = useState(1);
  const [ccPref, setCcPref] = useState(() => readPref("iep-cc", "auto")); // auto (pelo local de acesso) | idioma | off
  const [mini, setMini] = useState(false);
  const [theater, setTheater] = useState(() => readPref("iep-theater", false));
  const [isFull, setIsFull] = useState(false);
  const stageRef = useRef(null);
  // link com minuto (?aula=...&t=12:30), usado pela In'Nova IA: a aula abre já no ponto da explicação
  const inicioLink = useMemo(() => {
    const t = params.get("t") || "";
    const p = t.split(":").map(Number);
    return p.some(Number.isNaN) ? 0 : p.length === 2 ? p[0] * 60 + p[1] : p.length === 3 ? p[0] * 3600 + p[1] * 60 + p[2] : Number(t) || 0;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const playerRef = useRef(null);
  const [watch, setWatch] = useState(null); // tempo assistido / pausas da aula atual
  const [upNext, setUpNext] = useState(null); // { x, left } contagem para a próxima aula
  const [autoplayId, setAutoplayId] = useState(""); // aula aberta pela reprodução progressiva (já começa tocando)
  const tr = useRef({ playing: false, pending: 0, pauses: 0, pos: 0, maxPos: 0, sending: false });

  useEffect(() => { api.get(`/courses/${id}/exam/status`).then((r) => setExamSt(r.data)).catch(() => setExamSt(null)); }, [id, completed.length]);
  useEffect(() => { api.get("/me/watermark").then((r) => setWatermark(r.data)).catch(() => {}); }, []);
  useEffect(() => { api.get(`/courses/${id}/my-marks`).then((r) => setMarks(r.data)).catch(() => {}); }, [id]);
  useEffect(() => { const f = () => setIsFull(!!document.fullscreenElement); document.addEventListener("fullscreenchange", f); return () => document.removeEventListener("fullscreenchange", f); }, []);

  useEffect(() => {
    Promise.all([api.get(`/courses/${id}`), api.get(`/progress/${id}`)])
      .then(([c, p]) => {
        if (preview) {
          // pré-visualização: o que o aluno vê; a aula pedida em ?aula= aparece mesmo em rascunho
          const want = params.get("aula");
          c.data.modules = (c.data.modules || [])
            .map((m) => ({ ...m, lessons: (m.lessons || []).filter((l) => (l.published !== false && m.published !== false) || l.id === want) }))
            .filter((m) => m.published !== false || m.lessons.length);
        }
        setCourse(c.data);
        const done = p.data.completed_lessons || [];
        setCompleted(done);
        // abre a aula do link (?aula=) ou a primeira não concluída
        const want = params.get("aula");
        const flat = c.data.modules.flatMap((m) => m.lessons.map((l) => ({ moduleId: m.id, lesson: l })));
        const start = flat.find((x) => x.lesson.id === want) || flat.find((x) => !done.includes(x.lesson.id) && !x.lesson.locked) || flat[0];
        if (start) { setActive(start); setOpenModules({ [start.moduleId]: true }); }
      })
      .catch(() => navigate(backPath));
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const flat = useMemo(() => (course?.modules || []).flatMap((m) => m.lessons.map((l) => ({ moduleId: m.id, lesson: l }))), [course]);
  const allLessons = flat.map((x) => x.lesson.id);
  const doneVisible = allLessons.filter((x) => completed.includes(x)).length;
  const pct = allLessons.length ? Math.round((doneVisible / allLessons.length) * 100) : 0;
  const idx = active ? flat.findIndex((x) => x.lesson.id === active.lesson.id) : -1;
  const next = idx >= 0 ? flat[idx + 1] : null;
  const examBlocksCert = examSt?.enabled && examSt.required_for_certificate && !examSt.passed;

  // dados sociais da aula atual
  useEffect(() => {
    if (!active) return;
    setSocial(null); setRate(1);
    api.get(`/courses/${id}/lessons/${active.lesson.id}/social`).then((r) => setSocial(r.data)).catch(() => setSocial(null));
    if (params.get("aula") !== active.lesson.id) setParams({ aula: active.lesson.id }, { replace: true });
  }, [active?.lesson.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- tempo de permanência, pausas e "não pular" ----
  const rules = active && isStudentLesson(active.lesson, preview) && !active.lesson.locked ? active.lesson : null;
  // quem já concluiu o curso navega livre: sem "não avançar o vídeo", sem limite de pausas e sem tempo mínimo
  const livre = !!course?.free_navigation;
  // tempo assistido é registrado em toda aula de vídeo/áudio (aparece em Detalhes da matrícula → carga horária realizada)
  const tracked = !!rules && ["video", "audio"].includes(lessonType(rules));
  const sendWatch = useCallback(async () => {
    const t = tr.current;
    if (!active || t.sending || (!t.pending && !t.pauses)) return;
    const body = { seconds: t.pending, pauses: t.pauses, position: Math.floor(t.pos) };
    t.pending = 0; t.pauses = 0; t.sending = true;
    try { const r = await api.post(`/courses/${id}/lessons/${active.lesson.id}/watch`, body); setWatch(r.data); }
    catch (e) { /* tenta de novo no próximo envio */ }
    finally { t.sending = false; }
  }, [active?.lesson.id, id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    tr.current = { playing: false, pending: 0, pauses: 0, pos: 0, maxPos: 0, sending: false };
    setWatch(null);
    if (!tracked) return undefined;
    api.get(`/courses/${id}/lessons/${active.lesson.id}/watch`).then((r) => {
      setWatch(r.data); tr.current.maxPos = r.data.max_position || 0;
    }).catch(() => {});
    // conta 1 s a cada segundo com o vídeo tocando e a aba visível; envia a cada 10 s
    const tick = setInterval(() => {
      const t = tr.current;
      if (t.playing && document.visibilityState === "visible") {
        t.pending += 1;
        if (t.pending >= 10) sendWatch();
      }
    }, 1000);
    const onHide = () => { if (document.visibilityState !== "visible") sendWatch(); };
    document.addEventListener("visibilitychange", onHide);
    return () => { clearInterval(tick); document.removeEventListener("visibilitychange", onHide); sendWatch(); };
  }, [active?.lesson.id, tracked]); // eslint-disable-line react-hooks/exhaustive-deps

  const onPlay = () => { tr.current.playing = true; };
  const onPause = () => {
    const t = tr.current;
    t.playing = false;
    if (!rules || livre || !(rules.max_pauses > 0)) return;
    const used = (watch?.pauses || 0) + t.pauses;
    if (used >= rules.max_pauses) {
      toast.error(`Esta aula permite no máximo ${rules.max_pauses} pausa(s). O vídeo vai continuar.`);
      playerRef.current?.play();
      return;
    }
    t.pauses += 1;
    toast(`Pausa ${used + 1} de ${rules.max_pauses}.`);
    sendWatch();
  };
  const onTime = (sec) => {
    const t = tr.current;
    if (!livre && rules?.view_mode === "no_skip" && sec > t.maxPos + 3) {
      playerRef.current?.seek(Math.floor(t.maxPos));
      toast.error("Nesta aula não é possível avançar o vídeo. Assista em sequência.");
      return;
    }
    t.pos = sec;
    if (sec > t.maxPos) t.maxPos = sec;
  };
  const audioSeek = (e) => {
    const el = e.currentTarget, t = tr.current;
    if (rules?.view_mode === "no_skip" && el.currentTime > t.maxPos + 3) {
      el.currentTime = t.maxPos;
      toast.error("Nesta aula não é possível avançar o áudio.");
    }
  };
  const needMore = !livre && tracked && watch && watch.required > 0 && !watch.can_complete;

  // reprodução progressiva: contagem regressiva e abre a próxima aula já tocando
  useEffect(() => {
    if (!upNext) return undefined;
    if (upNext.left <= 0) { setAutoplayId(upNext.x.lesson.id); setUpNext(null); open(upNext.x); return undefined; }
    const t = setTimeout(() => setUpNext((u) => u && { ...u, left: u.left - 1 }), 1000);
    return () => clearTimeout(t);
  }, [upNext]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setUpNext((u) => (u && u.x.lesson.id === active?.lesson.id ? u : null)); }, [active?.lesson.id]);
  const autoplayNow = !!active && autoplayId === active.lesson.id;
  // legenda automática: idioma do país de onde o aluno está acessando (ou o navegador, se não identificado)
  const autoLang = watermark?.caption_lang || (navigator.language || "pt").slice(0, 2).toLowerCase();
  const cc = ccPref === "auto" ? autoLang : ccPref;
  // o idioma entra no link só na abertura da aula (trocar depois não recarrega o vídeo)
  const ccAtOpen = useMemo(() => cc, [active?.lesson.id, !!watermark]); // eslint-disable-line react-hooks/exhaustive-deps
  const chooseCc = (v) => {
    setCcPref(v); savePref("iep-cc", v);
    const nome = v === "off" ? "desligada" : v === "auto" ? `automática (${CC_LANGS.find((x) => x[0] === autoLang)?.[1] || autoLang})` : CC_LANGS.find((x) => x[0] === v)?.[1];
    toast.success(`Legenda ${nome}`);
  };

  const upNextCard = upNext && (
    <div className="absolute inset-0 z-30 grid place-items-center bg-black/75 text-white text-center px-6" data-testid="up-next">
      <div>
        <div className="text-xs uppercase tracking-wider text-white/70">Próxima aula em {upNext.left}s</div>
        <div className="font-display text-lg sm:text-xl font-bold mt-1 max-w-lg">{upNext.x.lesson.title}</div>
        <div className="flex justify-center gap-2 mt-4">
          <button onClick={() => setUpNext(null)} className="rounded-lg border border-white/40 px-4 py-2 text-sm hover:bg-white/10" data-testid="up-next-cancel">Cancelar</button>
          <button onClick={() => setUpNext((u) => ({ ...u, left: 0 }))} className="rounded-lg bg-[#1E9E2A] px-4 py-2 text-sm font-semibold hover:bg-green-600" data-testid="up-next-go">Assistir agora</button>
        </div>
      </div>
    </div>
  );

  // vídeo hospedado na plataforma: pede um link temporário só desta aluna
  const [hostedSrc, setHostedSrc] = useState({ id: "", url: "", error: "" });
  const hostedLesson = active && lessonType(active.lesson) === "video" && active.lesson.video_source === "upload" && !active.lesson.locked ? active.lesson.id : "";
  useEffect(() => {
    setHostedSrc({ id: "", url: "", error: "" });
    if (!hostedLesson) return;
    api.get(`/courses/${id}/lessons/${hostedLesson}/video`)
      .then((r) => setHostedSrc({ id: hostedLesson, url: r.data.url, error: "" }))
      .catch((e) => setHostedSrc({ id: hostedLesson, url: "", error: e.response?.data?.detail || "Vídeo não disponível." }));
  }, [hostedLesson, id]);

  const open = (x) => { setActive(x); setOpenModules((p) => ({ ...p, [x.moduleId]: true })); window.scrollTo({ top: 0, behavior: "smooth" }); };

  // Recarrega as aulas do servidor: ao concluir uma aula, a próxima é liberada lá (sequência de iniciante) e a lista
  // da tela precisa saber disso, senão a próxima continua aparecendo travada e o "avançar automaticamente" não avança.
  const refreshCourse = async () => {
    if (preview) return flat;
    try {
      const c = await api.get(`/courses/${id}`);
      setCourse(c.data);
      return (c.data.modules || []).flatMap((m) => m.lessons.map((l) => ({ moduleId: m.id, lesson: l })));
    } catch (e) { return flat; }
  };
  const setDone = async (lessonId, value) => {
    try {
      const res = await api.post("/progress", { course_id: id, lesson_id: lessonId, completed: value });
      setCompleted(res.data.completed_lessons);
      return await refreshCourse();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao atualizar progresso."); return null; }
  };
  const toggleComplete = async (lessonId) => {
    const isDone = completed.includes(lessonId);
    if (await setDone(lessonId, !isDone)) toast.success(isDone ? "Aula desmarcada." : "Aula concluída! 🎉");
  };

  const onEnded = useCallback(async () => {
    if (!active) return;
    tr.current.playing = false;
    if (tracked) await sendWatch();
    let lista = flat;
    if (!completed.includes(active.lesson.id)) {
      lista = await setDone(active.lesson.id, true);
      if (!lista) return;
    }
    // a próxima aula vem da lista ATUALIZADA (já liberada pelo servidor)
    const i = lista.findIndex((x) => x.lesson.id === active.lesson.id);
    const prox = i >= 0 ? lista[i + 1] : null;
    toast.success("Aula concluída! 🎉");
    if (autoAdvance && prox && !prox.lesson.locked) setUpNext({ x: prox, left: 8 });
  }, [active, completed, autoAdvance, flat, tracked, sendWatch]); // eslint-disable-line react-hooks/exhaustive-deps

  const react = async (patch) => {
    try {
      const r = await api.post(`/courses/${id}/lessons/${active.lesson.id}/react`, patch);
      setSocial(r.data);
      if ("favorite" in patch || "watch_later" in patch) api.get(`/courses/${id}/my-marks`).then((x) => setMarks(x.data)).catch(() => {});
    } catch (e) { toast.error("Não foi possível registrar."); }
  };
  const share = () => {
    const url = `${window.location.origin}/app/curso/${id}?aula=${active.lesson.id}`;
    navigator.clipboard?.writeText(url); toast.success("Link da aula copiado.");
  };
  const changeRate = async (r) => {
    if (await playerRef.current?.setRate(r)) { setRate(r); toast.success(`Velocidade ${r}x`); }
    else toast.error("Velocidade indisponível para este vídeo.");
  };
  const toggleFull = () => {
    if (document.fullscreenElement) document.exitFullscreen?.();
    else stageRef.current?.requestFullscreen?.().catch(() => toast.error("Tela cheia indisponível neste navegador."));
  };

  const shownModules = useMemo(() => (course?.modules || []).map((m) => ({
    ...m,
    lessons: m.lessons.filter((l) => (!q || l.title.toLowerCase().includes(q.toLowerCase()))
      && (filter === "todas" || (filter === "favoritas" ? marks.favorite.includes(l.id) : marks.watch_later.includes(l.id)))),
  })).filter((m) => m.lessons.length || (!q && filter === "todas")), [course, q, filter, marks]);

  if (!course) return <div className="grid place-items-center h-[60vh]"><Loader2 className="h-6 w-6 animate-spin text-[#232D4F]" /></div>;
  // curso presencial sem aulas online: mostra data, horário e local da turma
  if (isPresencial(course) && flat.length === 0 && !preview && backPath === "/app" && (course.event_end || course.event_date) && (course.event_end || course.event_date) < new Date().toLocaleDateString("sv-SE")) {
    // presencial já realizado: fica só no histórico de "Meus cursos"
    setTimeout(() => navigate("/app", { replace: true }), 0);
    return null;
  }
  if (isPresencial(course) && flat.length === 0) return (
    <main className="max-w-3xl mx-auto w-full px-4 md:px-6 py-5 space-y-5" data-testid="presencial-classroom">
      <button onClick={() => navigate(backPath)} className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#1E9E2A] bg-green-50 border border-green-200 rounded-md px-3 py-1.5 hover:bg-[#1E9E2A] hover:text-white"><ArrowLeft className="h-4 w-4" /> Voltar aos cursos</button>
      <h1 className="font-display text-2xl font-bold text-slate-900">{course.title}</h1>
      {(course.event_end || course.event_date) && (course.event_end || course.event_date) < new Date().toLocaleDateString("sv-SE") && (
        <div className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 text-emerald-700 px-3 py-1 text-sm font-bold" data-testid="presencial-realizado">✓ Curso realizado</div>
      )}
      <PresencialInfo c={course} />
      {course.description && <p className="text-slate-600 whitespace-pre-line">{course.description}</p>}
      {(course.bonus_courses || []).length > 0 && <PresencialBonus ids={course.bonus_courses.map((b) => b.course_id)} base={backPath} />}
    </main>
  );

  const ltype = active ? lessonType(active.lesson) : null;
  const hosted = active && ltype === "video" && active.lesson.video_source === "upload";
  const embed = active && ltype === "video" && !hosted ? toEmbedUrl(active.lesson.video_url) : null;
  const liveAt = active?.lesson.live_at ? new Date(active.lesson.live_at) : null;
  const liveSoon = liveAt ? liveAt.getTime() - Date.now() : null;
  const activeDone = active && completed.includes(active.lesson.id);
  const moduleTitle = active ? course.modules.find((m) => m.id === active.moduleId)?.title : "";
  const iconBtn = "h-10 min-w-[2.5rem] px-3 grid place-items-center whitespace-nowrap rounded-lg bg-white border border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-colors";

  const sideIcons = active && social && (
    <div className="absolute right-3 top-3 z-20 flex flex-col gap-2">
      {[
        [Heart, social.favorite, "Favoritar", () => react({ favorite: !social.favorite }), "fav"],
        [Clock, social.watch_later, "Assistir depois", () => react({ watch_later: !social.watch_later }), "later"],
        [Send, false, "Compartilhar", share, "share"],
      ].map(([I, on, t, fn, k]) => (
        <button key={k} onClick={fn} title={t} data-testid={`stage-${k}`}
          className={`h-10 w-10 grid place-items-center rounded-full backdrop-blur ${on ? "bg-[#1E9E2A] text-white" : "bg-black/45 text-white hover:bg-black/65"}`}>
          <I className={`h-5 w-5 ${on && k === "fav" ? "fill-white" : ""}`} />
        </button>
      ))}
    </div>
  );

  return (
    <main className={`${theater ? "max-w-[1600px]" : "max-w-7xl"} mx-auto w-full px-4 md:px-6 py-5`}>
      <button onClick={() => navigate(backPath)} className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#1E9E2A] bg-green-50 border border-green-200 rounded-md px-3 py-1.5 hover:bg-[#1E9E2A] hover:text-white hover:border-[#1E9E2A] transition-colors mb-3">
        <ArrowLeft className="h-4 w-4" /> Voltar aos cursos
      </button>
      {!preview && <HelpVideo moment="primeira_aula" label="Ver vídeo: como usar a sala de aula" title="Como usar a sala de aula" className="ml-3 mb-3" />}
      {preview && active && (active.lesson.published === false || course.modules.find((m) => m.id === active.moduleId)?.published === false) && (
        <div className="mb-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" data-testid="preview-draft">
          👁️ Pré-visualização: esta aula está em <strong>rascunho</strong> e ainda não aparece para os alunos.
        </div>
      )}
      {course.not_started && (
        <div className="mb-4 rounded-xl border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-800" data-testid="course-not-started">
          📅 As aulas deste curso começam em <strong>{new Date(course.starts_at).toLocaleString("pt-BR", { dateStyle: "long", timeStyle: "short" })}</strong>. Até lá, o conteúdo fica fechado.
        </div>
      )}

      <div className={`grid gap-6 ${theater ? "grid-cols-1" : "lg:grid-cols-[1fr_380px]"}`}>
        {/* Aula */}
        <div className="space-y-4 min-w-0 animate-fade-up">
          {active && (
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-[#1E9E2A]">{moduleTitle}</div>
                <h1 className="font-display text-2xl font-bold text-slate-900 leading-tight">{active.lesson.title}</h1>
              </div>
              <DropdownMenu modal={false}>
                <DropdownMenuTrigger asChild><button className="p-2 rounded-lg text-slate-500 hover:bg-slate-100" data-testid="lesson-menu"><MoreVertical className="h-5 w-5" /></button></DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuItem disabled={!!active.lesson.locked || (!activeDone && needMore)} onSelect={() => toggleComplete(active.lesson.id)}>
                    {activeDone ? <><Circle className="h-4 w-4 mr-2" /> Desmarcar como concluída</> : <><CheckCircle2 className="h-4 w-4 mr-2" /> Marcar como concluída</>}
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={share}><Link2 className="h-4 w-4 mr-2" /> Copiar link da aula</DropdownMenuItem>
                  {next && <DropdownMenuItem onSelect={() => open(next)}><ArrowRight className="h-4 w-4 mr-2" /> Próxima aula</DropdownMenuItem>}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}

          {/* Palco */}
          {active && active.lesson.locked ? (
            <div className="rounded-xl border border-slate-200 bg-slate-50 aspect-video grid place-items-center text-center px-6" data-testid="lesson-locked">
              <div>
                <Lock className="h-10 w-10 mx-auto mb-3 text-slate-400" />
                <div className="font-display text-lg font-semibold text-slate-700">Aula bloqueada</div>
                <p className="text-sm text-slate-500 mt-1 max-w-md">{active.lesson.locked}</p>
              </div>
            </div>
          ) : active && ltype === "audio" ? (
            <div className="rounded-xl shadow-lg p-6 text-white" style={{ background: "linear-gradient(135deg,#232D4F,#1A2340 60%,#1E9E2A 160%)" }} data-testid="lesson-audio">
              <div className="flex items-center gap-3 mb-4"><Headphones className="h-8 w-8" /><div className="font-display text-lg font-semibold">{active.lesson.title}</div></div>
              {upNext && <div className="relative h-40 rounded-lg overflow-hidden mb-3">{upNextCard}</div>}
              <audio controls autoPlay={autoplayNow} src={active.lesson.audio_url} className="w-full" controlsList="nodownload" onEnded={onEnded}
                onPlay={onPlay} onPause={(e) => { if (!e.currentTarget.ended) onPause(); }}
                onTimeUpdate={(e) => onTime(e.currentTarget.currentTime)} onSeeking={audioSeek} />
            </div>
          ) : active && ltype === "live" ? (
            <div className="rounded-xl shadow-lg p-6 sm:p-8 text-white text-center" style={{ background: "linear-gradient(135deg,#232D4F,#1A2340 60%,#1E9E2A 160%)" }} data-testid="lesson-live">
              <Radio className="h-10 w-10 mx-auto mb-3" />
              <div className="font-display text-xl font-bold">{active.lesson.title}</div>
              {liveAt && <div className="mt-2 text-white/90 flex items-center justify-center gap-2"><CalendarClock className="h-4 w-4" /> {liveAt.toLocaleString("pt-BR", { dateStyle: "full", timeStyle: "short" })}</div>}
              {liveSoon !== null && liveSoon > 0 && (
                <div className="mt-1 text-sm text-white/75">Começa em {liveSoon > 86400000 ? `${Math.ceil(liveSoon / 86400000)} dia(s)` : liveSoon > 3600000 ? `${Math.floor(liveSoon / 3600000)} h ${Math.floor((liveSoon % 3600000) / 60000)} min` : `${Math.max(1, Math.ceil(liveSoon / 60000))} min`}</div>
              )}
              <a href={active.lesson.live_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 mt-5 rounded-lg bg-white text-[#232D4F] font-semibold px-5 py-2.5 hover:bg-green-50">
                <ExternalLink className="h-4 w-4" /> {liveSoon !== null && liveSoon > 15 * 60000 ? "Abrir link do encontro" : "Entrar agora"}
              </a>
            </div>
          ) : active && (ltype === "video" || ltype === "embed") ? (
            <div className={mini ? "aspect-video rounded-xl border-2 border-dashed border-slate-200 grid place-items-center text-sm text-slate-400" : ""}>
              {mini && <span>Vídeo no mini player ↘</span>}
              <div ref={stageRef} data-testid="lesson-video-player-frame"
                className={mini
                  ? "fixed bottom-4 right-4 z-50 w-[340px] sm:w-[420px] aspect-video rounded-xl overflow-hidden shadow-2xl ring-1 ring-black/20"
                  : "relative rounded-xl overflow-hidden bg-black aspect-video shadow-lg"}>
                {ltype === "embed" ? (
                  <iframe src={active.lesson.embed_url} title={active.lesson.title} className="w-full h-full bg-white"
                    sandbox="allow-scripts allow-same-origin allow-popups allow-forms allow-presentation" />
                ) : hosted && hostedSrc.url ? (
                  <VideoStage ref={playerRef} native src={hostedSrc.url} autoPlay={autoplayNow} title={active.lesson.title}
                    watermark={preview || active.lesson.show_watermark === false ? null : watermark}
                    onEnded={onEnded} onPlay={onPlay} onPause={onPause} onTime={onTime} overlay={<>{!mini && sideIcons}{upNextCard}</>} />
                ) : hosted && !hostedSrc.error ? (
                  <div className="w-full h-full grid place-items-center"><Loader2 className="h-8 w-8 animate-spin text-white/60" /></div>
                ) : embed ? (
                  <VideoStage ref={playerRef} src={playerSrc(embed, autoplayNow, ccAtOpen, active.lesson.id === params.get("aula") ? inicioLink : 0)} captions={cc} title={active.lesson.title} watermark={preview || active.lesson.show_watermark === false ? null : watermark}
                    onEnded={onEnded} onPlay={onPlay} onPause={onPause} onTime={onTime} overlay={<>{!mini && sideIcons}{upNextCard}</>} />
                ) : (
                  <div className="w-full h-full grid place-items-center text-slate-400 text-center px-6"><div><Video className="h-10 w-10 mx-auto mb-2 opacity-50" /><p className="text-sm">{(hosted && hostedSrc.error) || "Vídeo não disponível para esta aula."}</p></div></div>
                )}
                {mini && <button onClick={() => setMini(false)} className="absolute top-2 left-2 z-30 h-7 w-7 grid place-items-center rounded-full bg-black/60 text-white" title="Voltar ao player"><X className="h-4 w-4" /></button>}
              </div>
            </div>
          ) : null}

          {/* Barra de ações */}
          {active && !active.lesson.locked && (active.lesson.duration || (tracked && watch && !livre && (watch.required > 0 || watch.max_pauses > 0 || rules.view_mode === "no_skip"))) && (
            <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600 flex flex-wrap items-center gap-x-5 gap-y-2" data-testid="lesson-rules">
              {active.lesson.duration && <span className="inline-flex items-center gap-1.5"><Clock3 className="h-4 w-4 text-[#1E9E2A]" /> Duração: <strong>{active.lesson.duration}</strong></span>}
              {tracked && watch && !livre && watch.required > 0 && (
                <div className="flex items-center gap-2 min-w-[220px] flex-1">
                  <Timer className="h-4 w-4 text-[#1E9E2A]" />
                  <span>Tempo assistido: <strong>{fmtMin(Math.min(watch.watched, watch.required))}</strong> de {fmtMin(watch.required)}</span>
                  <div className="flex-1 h-1.5 rounded-full bg-slate-200 overflow-hidden min-w-[60px]"><div className="h-full bg-[#1E9E2A]" style={{ width: `${Math.min(100, (watch.watched / watch.required) * 100)}%` }} /></div>
                </div>
              )}
              {tracked && watch && !livre && watch.max_pauses > 0 && <span>Pausas: <strong>{watch.pauses}</strong> de {watch.max_pauses}</span>}
              {tracked && watch && !livre && rules.view_mode === "no_skip" && <span>⏩ Avanço do vídeo bloqueado</span>}
            </div>
          )}

          {active && social && (
            <div className="flex flex-wrap items-center gap-2" data-testid="lesson-actions">
              <button onClick={() => react({ vote: social.my_vote === "like" ? "" : "like" })} className={`${iconBtn} !flex items-center gap-2 ${social.my_vote === "like" ? "!bg-[#232D4F] !text-white" : ""}`} data-testid="btn-like">
                <ThumbsUp className="h-4 w-4" /> {social.likes} {social.likes === 1 ? "like" : "likes"}
              </button>
              <button onClick={() => react({ vote: social.my_vote === "dislike" ? "" : "dislike" })} className={`${iconBtn} ${social.my_vote === "dislike" ? "!bg-slate-700 !text-white" : ""}`} title="Não gostei" data-testid="btn-dislike">
                <ThumbsDown className="h-4 w-4" />
              </button>
              <button onClick={() => react({ love: !social.my_love })} className={`${iconBtn} !flex items-center gap-2 ${social.my_love ? "!bg-rose-500 !text-white !border-rose-500" : ""}`} data-testid="btn-love">
                <Heart className={`h-4 w-4 ${social.my_love ? "fill-white" : ""}`} /> Amei!{social.loves ? ` · ${social.loves}` : ""}
              </button>
              <div className="flex-1" />
              {ltype === "video" && (
                <>
                  <button onClick={() => { const v = !autoAdvance; setAutoAdvance(v); savePref("iep-autoplay", v); toast.success(v ? "Avançar automaticamente: ligado" : "Avançar automaticamente: desligado"); }}
                    className={`${iconBtn} !flex items-center gap-2`} title="Ao terminar o vídeo, conclui a aula e vai para a próxima" data-testid="btn-autoplay">
                    <span className={`relative h-4 w-7 rounded-full transition-colors ${autoAdvance ? "bg-[#1E9E2A]" : "bg-slate-300"}`}>
                      <span className={`absolute top-0.5 h-3 w-3 rounded-full bg-white transition-all ${autoAdvance ? "left-3.5" : "left-0.5"}`} />
                    </span>
                    <span className="text-xs hidden sm:inline">Auto</span>
                  </button>
                  {!hosted && <DropdownMenu modal={false}>
                    <DropdownMenuTrigger asChild>
                      <button className={`${iconBtn} !flex items-center gap-1.5 text-xs font-semibold ${cc !== "off" ? "!text-[#232D4F]" : "!text-slate-400"}`} title="Legendas" data-testid="btn-captions">
                        <Captions className="h-4 w-4" /> {cc === "off" ? "" : cc.toUpperCase()}
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-60">
                      <DropdownMenuItem onSelect={() => chooseCc("auto")} className={ccPref === "auto" ? "font-bold" : ""}>
                        <div>
                          <div>Automática · {CC_LANGS.find((x) => x[0] === autoLang)?.[1] || autoLang}</div>
                          {watermark?.country && <div className="text-[11px] font-normal text-slate-500">Acessando de: {COUNTRY_NAME(watermark.country)}</div>}
                        </div>
                      </DropdownMenuItem>
                      {CC_LANGS.map(([k, l]) => <DropdownMenuItem key={k} onSelect={() => chooseCc(k)} className={ccPref === k ? "font-bold" : ""}>{l}</DropdownMenuItem>)}
                      <DropdownMenuItem onSelect={() => chooseCc("off")} className={ccPref === "off" ? "font-bold" : ""}>Desligar legenda</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>}
                  <DropdownMenu modal={false}>
                    <DropdownMenuTrigger asChild><button className={`${iconBtn} text-sm font-semibold`} title="Velocidade" data-testid="btn-speed">{rate}x</button></DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {[0.75, 1, 1.25, 1.5, 1.75, 2].map((r) => <DropdownMenuItem key={r} onSelect={() => changeRate(r)} className={r === rate ? "font-bold" : ""}>{r}x</DropdownMenuItem>)}
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <button onClick={() => setMini((v) => !v)} className={`${iconBtn} ${mini ? "!bg-[#232D4F] !text-white" : ""}`} title="Mini player" data-testid="btn-mini"><PictureInPicture2 className="h-4 w-4" /></button>
                  <button onClick={() => { const v = !theater; setTheater(v); savePref("iep-theater", v); }} className={`${iconBtn} hidden lg:grid ${theater ? "!bg-[#232D4F] !text-white" : ""}`} title="Modo teatro" data-testid="btn-theater"><RectangleHorizontal className="h-4 w-4" /></button>
                  <button onClick={toggleFull} className={iconBtn} title="Tela cheia" data-testid="btn-fullscreen">{isFull ? <Minimize className="h-4 w-4" /> : <Maximize className="h-4 w-4" />}</button>
                </>
              )}
              {!active.lesson.locked && <Button onClick={() => toggleComplete(active.lesson.id)} disabled={!activeDone && needMore}
                title={!activeDone && needMore ? `Assista pelo menos ${fmtMin(watch.required)} para concluir` : ""}
                className="h-11 sm:h-10 w-full sm:w-auto order-first sm:order-none text-white" style={{ background: activeDone ? "#64748B" : "#1E9E2A" }} data-testid="btn-mark-lesson-complete">
                {activeDone ? <><CheckCircle2 className="h-4 w-4 mr-1.5" /> Concluída</> : <><Circle className="h-4 w-4 mr-1.5" /> Concluir aula</>}
              </Button>}
            </div>
          )}

          {/* Descrição / texto */}
          {active && !active.lesson.locked && (active.lesson.description || (ltype === "text" && active.lesson.html)) && (
            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm">
              {active.lesson.description && <p className="text-slate-600 leading-relaxed text-sm whitespace-pre-line">{active.lesson.description}</p>}
              {ltype === "text" && active.lesson.html && (
                <div className="lesson-html mt-3 text-slate-700 text-[15px]" data-testid="lesson-text" dangerouslySetInnerHTML={{ __html: active.lesson.html }} />
              )}
            </div>
          )}

          {active && social && !active.lesson.locked && (
            <LessonTabs key={active.lesson.id} courseId={id} moduleId={active.moduleId} lesson={active.lesson} playerRef={playerRef}
              counts={social} onCounts={(c) => setSocial((p) => ({ ...p, ...c }))}
              fileLabel={ltype === "file" ? "Arquivos da aula" : "Materiais complementares"} />
          )}
        </div>

        {/* Barra lateral */}
        <aside className={`self-start space-y-4 ${theater ? "" : "lg:sticky lg:top-20"}`}>
          <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <Ring pct={pct} />
              <div className="min-w-0 flex-1">
                <h2 className="font-display font-bold text-slate-900 leading-snug line-clamp-2">{course.title}</h2>
                <p className="text-sm text-slate-500">{doneVisible} de {allLessons.length} aulas</p>
              </div>
              {next && <button onClick={() => open(next)} title="Próxima aula" className="h-9 w-9 grid place-items-center rounded-full text-slate-600 hover:bg-slate-100" data-testid="btn-next-lesson"><ArrowRight className="h-5 w-5" /></button>}
            </div>
            <div className="relative mt-3">
              <Search className="h-4 w-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Pesquisar conteúdos..." className="w-full h-10 rounded-lg border border-slate-200 bg-slate-50 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E9E2A]/40" data-testid="lesson-search" />
            </div>
            <div className="flex gap-1 mt-2 bg-slate-100 rounded-lg p-1">
              {[["todas", "Todas"], ["favoritas", `Favoritas (${marks.favorite.length})`], ["depois", `Depois (${marks.watch_later.length})`]].map(([k, l]) => (
                <button key={k} onClick={() => setFilter(k)} className={`flex-1 py-1 rounded-md text-xs font-medium ${filter === k ? "bg-white text-[#232D4F] shadow-sm" : "text-slate-500"}`}>{l}</button>
              ))}
            </div>
            {!preview && pct === 100 && allLessons.length > 0 && course.certificate_enabled !== false && !examBlocksCert && (
              <button data-testid="btn-emit-certificate" onClick={() => navigate(`/app/certificado/${id}`)}
                className="mt-3 w-full flex items-center justify-center gap-2 rounded-lg py-2.5 text-white text-sm font-semibold" style={{ background: "linear-gradient(135deg,#232D4F,#1E9E2A)" }}>
                <Award className="h-4 w-4" /> Emitir certificado
              </button>
            )}
          </div>

          {examSt?.enabled && (
            <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm" data-testid="exam-card">
              <div className="flex items-center gap-2 font-semibold text-slate-800"><ClipboardCheck className="h-4 w-4 text-[#1E9E2A]" /> {examSt.title}</div>
              <p className="text-xs text-slate-500 mt-1">{examSt.num_questions} questões · nota mínima {examSt.pass_score}%{examSt.time_limit ? ` · ${examSt.time_limit} min` : ""}</p>
              {examSt.passed ? <p className="text-sm text-green-700 font-semibold mt-2">Aprovado(a) — melhor nota {examSt.best_score}%</p>
                : examSt.best_score !== null && <p className="text-sm text-slate-600 mt-2">Melhor nota: {examSt.best_score}%</p>}
              {!examSt.can_start && !examSt.passed && examSt.reason && <p className="text-xs text-amber-700 mt-2">{examSt.reason}</p>}
              {!preview && (examSt.can_start || examSt.passed) && (
                <Button onClick={() => navigate(`/app/curso/${id}/prova`)} className="w-full mt-3 text-white" style={{ background: examSt.passed ? "#64748B" : "#232D4F" }} data-testid="btn-open-exam">
                  {examSt.passed ? "Ver prova" : examSt.in_progress ? "Continuar a prova" : "Fazer a prova final"}
                </Button>
              )}
            </div>
          )}

          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden max-h-[70vh] overflow-y-auto iep-scroll">
            {shownModules.length === 0 && <p className="p-4 text-sm text-slate-400">Nenhuma aula encontrada.</p>}
            {shownModules.map((m) => {
              const full = course.modules.find((x) => x.id === m.id);
              const isOpen = openModules[m.id] || !!q || filter !== "todas";
              const doneCount = full.lessons.filter((l) => completed.includes(l.id)).length;
              const mp = full.lessons.length ? (doneCount / full.lessons.length) * 100 : 0;
              return (
                <div key={m.id} className="border-b border-slate-100 last:border-0">
                  <button onClick={() => setOpenModules((p) => ({ ...p, [m.id]: !p[m.id] }))} className="w-full px-4 pt-3 pb-2.5 hover:bg-slate-50 text-left">
                    <div className="flex items-center gap-2">
                      <span className="flex-1 text-[15px] font-semibold text-slate-800">{m.title}</span>
                      <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                    </div>
                    <div className="flex items-center gap-2 mt-1.5">
                      <span className="text-[11px] text-slate-500 w-9">{doneCount}/{full.lessons.length}</span>
                      <div className="flex-1 h-1.5 rounded-full bg-slate-200 overflow-hidden"><div className="h-full bg-[#1E9E2A] rounded-full" style={{ width: `${mp}%` }} /></div>
                    </div>
                  </button>
                  {isOpen && (
                    <div className="pb-2 px-2">
                      {m.lessons.length === 0 && <p className="px-3 py-2 text-xs text-slate-400">Sem aulas.</p>}
                      {m.lessons.map((l) => {
                        const n = flat.findIndex((x) => x.lesson.id === l.id) + 1;
                        const done = completed.includes(l.id);
                        const isActive = active?.lesson.id === l.id;
                        const Icon = TYPE_ICON[lessonType(l)] || PlaySquare;
                        return (
                          <div key={l.id} data-testid={`lesson-item-${l.id}`}
                            className={`flex items-center gap-3 rounded-lg px-2 py-2.5 cursor-pointer ${isActive ? "bg-sky-50 ring-1 ring-[#232D4F]/15" : "hover:bg-slate-50"}`}
                            onClick={() => open({ moduleId: m.id, lesson: l })}>
                            <span className="text-xs font-mono text-slate-400 w-5 text-right">{String(n).padStart(2, "0")}</span>
                            <span className="relative shrink-0 text-slate-500">
                              {l.locked ? <Lock className="h-6 w-6 text-slate-300" strokeWidth={1.5} /> : <Icon className="h-6 w-6" strokeWidth={1.5} />}
                            </span>
                            <span className="flex-1 min-w-0" title={l.locked || ""}>
                              <span className={`block text-sm leading-snug ${isActive ? "text-[#232D4F] font-semibold" : l.locked ? "text-slate-400" : "text-slate-700"}`}>{l.title}</span>
                              {l.duration && <span className="mt-0.5 inline-flex items-center gap-1 text-[11px] text-slate-400"><Timer className="h-3 w-3" />{l.duration}</span>}
                            </span>
                            {!l.locked && <button onClick={(e) => { e.stopPropagation(); toggleComplete(l.id); }} title={done ? "Desmarcar" : "Marcar como concluída"}
                              className={`h-6 w-6 shrink-0 grid place-items-center rounded-full ${done ? "bg-[#1E9E2A] text-white" : "border border-slate-300 text-slate-300 hover:border-[#1E9E2A] hover:text-[#1E9E2A]"}`}>
                              <CheckCircle2 className="h-4 w-4" />
                            </button>}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </aside>
      </div>
    </main>
  );
}

/** Na tela do curso presencial: atalho para o curso online que a aluna ganhou de bônus. */
function PresencialBonus({ ids, base }) {
  const navigate = useNavigate();
  const [list, setList] = useState(null);
  useEffect(() => { api.get("/courses").then((r) => setList(r.data.filter((x) => ids.includes(x.id)))).catch(() => setList([])); }, [ids.join(",")]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!list) return null;
  if (!list.length) return <p className="text-sm text-slate-500">🎁 O seu curso online de bônus aparece em <strong>Meus cursos</strong>, na página inicial.</p>;
  return (
    <div className="space-y-2" data-testid="presencial-bonus">
      <div className="text-sm font-bold text-slate-800">🎁 Seu bônus online</div>
      {list.map((b) => (
        <button key={b.id} onClick={() => navigate(`${base}/curso/${b.id}`)} className="w-full text-left flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-3 hover:shadow-md transition">
          {b.banner ? <img src={b.banner} alt="" className="w-24 aspect-video object-cover rounded-md" /> : null}
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-slate-800 line-clamp-1">{b.title}</div>
            {typeof b.progress_percent === "number" && <div className="text-xs text-slate-500">{b.progress_percent}% concluído</div>}
          </div>
          <span className="rounded-md px-3 py-1.5 text-sm font-semibold text-white" style={{ background: "#1E9E2A" }}>Acessar</span>
        </button>
      ))}
    </div>
  );
}
